import React from 'react';
import {useColorScheme} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {Provider as PaperProvider} from 'react-native-paper';
import {AppNavigator} from './presentation/navigation/AppNavigator';
import {NotificationProvider} from './shared/components/NotificationProvider';
import {AuthProvider} from './presentation/context/AuthContext';
import {PWAInstallBanner} from './presentation/components/PWAInstallBanner';
import {PWAUpdateBanner} from './presentation/components/PWAUpdateBanner';

// Importar estilos globales para web
if (typeof window !== 'undefined') {
  require('./web-styles.css');
}

const App: React.FC = () => {
  const colorScheme = useColorScheme();

  // Configurar tema basado en el modo del sistema
  const theme = {
    dark: colorScheme === 'dark',
    colors: {
      primary: '#6366F1',
      onPrimary: '#FFFFFF',
      primaryContainer: colorScheme === 'dark' ? '#3B3B7A' : '#E0E0FF',
      onPrimaryContainer: colorScheme === 'dark' ? '#E0E0FF' : '#1A1A3D',
      secondary: '#8B5CF6',
      onSecondary: '#FFFFFF',
      secondaryContainer: colorScheme === 'dark' ? '#4A2D7A' : '#E8D5FF',
      onSecondaryContainer: colorScheme === 'dark' ? '#E8D5FF' : '#2A1A3D',
      tertiary: '#F59E0B',
      onTertiary: '#FFFFFF',
      error: '#F44336',
      onError: '#FFFFFF',
      errorContainer: colorScheme === 'dark' ? '#7A1F1F' : '#FFEBEE',
      onErrorContainer: colorScheme === 'dark' ? '#FFEBEE' : '#3D0F0F',
      background: colorScheme === 'dark' ? '#121212' : '#f3f4f6',
      onBackground: colorScheme === 'dark' ? '#FFFFFF' : '#000000',
      surface: colorScheme === 'dark' ? '#1E1E1E' : '#FFFFFF',
      onSurface: colorScheme === 'dark' ? '#FFFFFF' : '#000000',
      surfaceVariant: colorScheme === 'dark' ? '#2D2D2D' : '#F5F5F5',
      onSurfaceVariant: colorScheme === 'dark' ? '#B0B0B0' : '#666666',
      outline: colorScheme === 'dark' ? '#424242' : '#E5E7EB',
      outlineVariant: colorScheme === 'dark' ? '#2D2D2D' : '#E5E7EB',
      shadow: 'rgba(0, 0, 0, 0.2)',
      scrim: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
      inverseSurface: colorScheme === 'dark' ? '#FFFFFF' : '#1E1E1E',
      inverseOnSurface: colorScheme === 'dark' ? '#000000' : '#FFFFFF',
      inversePrimary: '#8B8BFF',
      elevation: {
        level0: 'transparent',
        level1: colorScheme === 'dark' ? '#1E1E1E' : '#FFFFFF',
        level2: colorScheme === 'dark' ? '#2D2D2D' : '#F5F5F5',
        level3: colorScheme === 'dark' ? '#3D3D3D' : '#F0F0F0',
        level4: colorScheme === 'dark' ? '#424242' : '#EBEBEB',
        level5: colorScheme === 'dark' ? '#4A4A4A' : '#E5E5E5',
      },
      surfaceDisabled: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      onSurfaceDisabled: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
      backdrop: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
    },
  };

  return (
    <SafeAreaProvider>
      <PaperProvider theme={theme}>
        <AuthProvider>
          <NotificationProvider>
            <AppNavigator />
            <PWAInstallBanner />
            <PWAUpdateBanner />
          </NotificationProvider>
        </AuthProvider>
      </PaperProvider>
    </SafeAreaProvider>
  );
};

export default App;

